/**
 * Target GP (JF 2026-10-16, option B): the AM types the GP % wanted; every mark-up of the quote is scaled by ONE factor
 * k until the sheet reaches it — the quoted fabric / trim prices (1 + price %), the other costs (1 + additional %) and
 * the CM factor are all multiplied by k, so the quote as a whole moves up or down. Yields and finance stay as they are.
 * Lines typed by hand keep their value (as with any coefficient change), so k is found by search, not by formula.
 */
import { totals } from "./costing.ts";
import type { QCoef, Sheet } from "./types.ts";

export function scaleCoef(c: QCoef, k: number): QCoef {
  return { ...c, fabPx: (1 + c.fabPx) * k - 1, trimPx: (1 + c.trimPx) * k - 1, add: (1 + c.add) * k - 1, cmMult: c.cmMult * k };
}
const r4 = (x: number) => Math.round(x * 1e4) / 1e4;

/** The coefficients that give `target` GP (0.30 = 30 %), and the factor used; null when no factor in 0.2–5 gets there. */
export function coefForGp(s: Sheet, target: number): { coef: QCoef; k: number; gpp: number } | null {
  const c = s.qcoef;
  if (!c || !(target < 1)) return null;
  const gppAt = (k: number) => totals({ ...s, qcoef: scaleCoef(c, k) }).gpp;
  let lo = 0.2, hi = 5;
  if (!(gppAt(lo) <= target && gppAt(hi) >= target)) return null;
  for (let i = 0; i < 60; i++) { const mid = (lo + hi) / 2; if (gppAt(mid) < target) lo = mid; else hi = mid; }
  const k = (lo + hi) / 2, raw = scaleCoef(c, k);
  // round to what the panel shows (4 decimals of a %) — then the GP lands within a hair of the target
  const coef = { ...raw, fabPx: r4(raw.fabPx), trimPx: r4(raw.trimPx), add: r4(raw.add), cmMult: Math.round(raw.cmMult * 1e4) / 1e4 };
  return { coef, k, gpp: totals({ ...s, qcoef: coef }).gpp };
}
